import { Injectable } from '@angular/core';

import { Observable, Subject, of } from 'rxjs';
import { AppSettings } from '../app.settings';
import { RestApiService } from '../services/rest-api.service';
import { CommonBindingDataService } from './common-binding-data.service';
import { NotificationsService } from './notifications.service';
import { StorageService } from './storage.service';
declare const google: any;

@Injectable()
export class UserCommonService {
  isMetadataLoaded = false;
  isCompanyRole = false;
  userMetaData: any;
  ssoFeature = false;
  scrollSectionToTop: Subject<any> = new Subject<any>();
  poweredByRedirectBeforeLoginUrl = AppSettings.DEFAULT_POWERED_BY_REDIRECT_URL;
  poweredByBeforeLogoUrl = AppSettings.DEFAULT_POWERED_BY_LOGO_URL;
  constructor(private restApiService: RestApiService,
    private commonService: CommonBindingDataService,
    private storageService: StorageService,
    private notificationsService: NotificationsService) {
    this.getAdminUserMetaData();
    if (this.storageService.getItemFromSessionStorage(AppSettings.TOKEN_KEY)) {
      if (AppSettings.isSessionClearRoute()) {
        this.clearAuthenticatedSession();
      } else {
        this.loadUserMetaData();
      }
    }
  }

  clearAuthenticatedSession(): void {
    this.storageService.removeAll();
    this.storageService.clearSessionStorage();
    AppSettings.HEADER_AUTHORIZATION_VALUE = null;
    this.notificationsService.canFetchNotification = false;
  }
  signOut(): Observable<any> {
    return this.restApiService.delete('/secure/signout', 'page-center');
  }

  signIn(data): Observable<any> {
    return this.restApiService.post('/user-signin', data, 'page-center');
  }

  ssoSignIn(data): Observable<any> {
    return this.restApiService.post('/sso/user-signin', data, 'page-center');
  }

  publicUserExportData(data): Observable<any> {
    return this.restApiService.post('/gdpr/export-details/file', data, 'page-center');
  }

  passwordChange(data): Observable<any> {
    return this.restApiService.put('/secure/user-profile/users/update-password', data, 'page-center');
  }

  updatePassword(data, userId: any): Observable<any> {
    return this.restApiService.put(`/secure/manage-users/update-password/${userId}`, data, 'page-center');
  }

  updatePasswordShuttleDriver(data, userId: any): Observable<any> {
    return this.restApiService.put(`/secure/bus-users/update-password/${userId}`, data, 'page-center');
  }

  resetPassword(data): Observable<any> {
    return this.restApiService.post(`/users/reset-password/verify-token`, data, 'page-center');
  }

  sendOtpEmailAndPhone(data): Observable<any> {
    return this.restApiService.post('/user-authentication/otp/send', data, 'page-center');
  }

  signInWithOtpEmailAndPhone(data): Observable<any> {
    return this.restApiService.post('/user-authentication/otp/signin', data, 'page-center');
  }

  setPassword(data): Observable<any> {
    return this.restApiService.post('/users/set-password', data, 'page-center');
  }

  getUserProfile(): Observable<any> {
    return this.restApiService.get('/secure/user-profile/users', 'page-center');
  }

  updateUserProfile(data): Observable<any> {
    return this.restApiService.put('/secure/user-profile/users', data, 'page-center');
  }

  forgotPassword(data): Observable<any> {
    return this.restApiService.post('/users/forgot-password', data, 'page-center');
  }

  validateOtp(data): Observable<any> {
    return this.restApiService.post('/users/verify-otp', data, 'page-center');
  }

  getServiceAreaId(): Observable<any> {
    return this.restApiService.get('/secure/tenants/servicearea', 'page-center');
  }

  getSSOLogin(): Observable<any> {
    return this.restApiService.get('/saml/ssologin', 'page-center');
  }

  getUserMetaData(): Observable<any> {
    return this.restApiService.get('/secure/user-metadata', 'page-center');
  }

  getPessangerDetails(userId, eventId?): Observable<any> {
    return this.restApiService.get(`/secure/admin-booking/passenger-details/${userId}?eventId=${eventId}`, 'page-center');
  }

  getUserMetaDataInsecure(): Observable<any> {
    return this.restApiService.get('/admin/metadata', 'page-center');
  }

  addLoadBookingDocument(data): Observable<any> {
    return this.restApiService.post(`/secure/file/import-file`, data);
  }

  getTrackMyLocationDetails(bookingId): Observable<any> {
    return this.restApiService.get('/track-my-location/' + bookingId, 'page-center');
  }

  getTrackMyLiveLocation(bookingId): Observable<any> {
    return this.restApiService.get('/track-my-location/live/' + bookingId);
  }

  /** The event saved filters are keyed by: the selected event of the session. Blank when none is selected. */
  getActiveEventId(): string {
    return this.commonService.getSelectedEventId() || '';
  }

  /** Saved filters are stored per user + screen + event. Without an event nothing is posted; the caller still applies its in-memory filters. */
  updateSavedFilter(data): Observable<any> {
    if (!data?.eventId) {
      return of(data);
    }
    return this.restApiService.post('/secure/save-filter', data);
  }

  updateFilterOpenClose(data, key?): Observable<any> {
    if (!data?.eventId) {
      return of(data);
    }
    return this.restApiService.post(`/secure/save-filter?filterKey=${key}`, data);
  }

  // for driver checkin grid filter
  getPassengerAllocatedLocationData(): Observable<any> {
    return this.restApiService.get(`/secure/locations?projection=grid&isLimit=false`, 'page-center');
  }

  /**
   *
   * @param screenCode : Screencode to get filter data
   * @param eventId : the active event: only the blob saved for that event is returned, never another event's or a screen-only one
   */
  getSavedFilters(screenCode: String, eventId?: String) {
    if (!eventId) {
      return null;
    }
    let filters: any[] = this.storageService.getItemFromSessionStorage(AppSettings.SAVE_FILTERS);
    filters = filters ? filters : [];
    const objects = filters.filter(ele => {
      return ele.screenCode === screenCode && ele.eventId === eventId;
    });
    if (objects.length > 0) {
      return objects[0];
    } else {
      return null;
    }
  }

  /** Same screen and same event. */
  private isSameFilterEntry(entry: any, other: any): boolean {
    return entry.screenCode === other.screenCode && (entry.eventId ?? null) === (other.eventId ?? null);
  }

  updateFilterDataToLocal(filterData: any, key?, val?) {
    // An entry without an event was never saved (see updateSavedFilter) and could not be restored.
    if (!filterData?.eventId) {
      return;
    }
    let filters: any[] = this.storageService.getItemFromSessionStorage(AppSettings.SAVE_FILTERS);
    filters = filters ? filters : [];
    const objects = filters.filter(ele => {
      return this.isSameFilterEntry(ele, filterData);
    });

    if (objects.length === 0) {
      filters.push(filterData);
    } else {
      const index = filters.findIndex(ele => this.isSameFilterEntry(ele, filterData));
      if (index >= 0) {
        if (key && val) {
          filters[index]['filterData'][key] = val;
        } else {
          filters[index] = filterData;
        }
      }
    }
    this.storageService.setSessionStorage(AppSettings.SAVE_FILTERS, filters);
  }

  getAdminUserMetaData() {
    this.getUserMetaDataInsecure().subscribe(result => {
      this.ssoFeature = !!result && (result.sso_feature === true || result.sso_feature === 'true');
      let decryptedGoogleBrowserKey = this.commonService.decrypt(result.googleBrowserAPIKey);
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${decryptedGoogleBrowserKey}&libraries=visualization,places,geometry`;
      document.getElementsByTagName('head')[0].appendChild(script);
      if (result.tenantInfo.poweredByName) {
        this.poweredByRedirectBeforeLoginUrl = result.tenantInfo.poweredByName;
        this.poweredByBeforeLogoUrl = AppSettings.GET_FILE_URL + '/' + result.tenantInfo.poweredByImageId;
      }
      setTimeout(() => {
        this.isMetadataLoaded = true;
      }, 2000);
    }, (error) => {
    });
  }

  setDefaultConstants(result: any) {
    const countryCodeObj = result.tenantConfigKeys.find(ele => {
      return ele.configKey === 'num_country_code';
    });

    if (countryCodeObj) {
      AppSettings.DEFAULT_COUNTRY_CODE = countryCodeObj.configValue;
    }
  }

  loadUserMetaData() {
    this.getUserMetaData().subscribe(result => {
      this.userMetaData = result;
      this.setDefaultConstants(result);
    }, (error) => {
    });
  }

  getGoogleMapAutoComplete(data): Observable<any> {
    return this.restApiService.post(`/secure/google-map/v2/autocomplete`,data, 'page-center');
  }

  getGoogleMapLatLng(placeId): Observable<any> {
    return this.restApiService.get(`/secure/google-map/geocode?placeId=${placeId}`, 'page-center');
  }

  getGoogleMapPlace(latLng): Observable<any> {
    return this.restApiService.get(`/secure/google-map/reverse-geocode?latLng=${latLng}`);
  }

  getGoogleMapRoute(data): Observable<any> {
    return this.restApiService.post(`/secure/google-map/v2/route`, data, 'page-center');
  }

  decodePolyline(encoded: string) {
    return google.maps.geometry.encoding.decodePath(encoded);
  }

}

